import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import moment from 'moment/moment'
import { Button } from '@resurs/midas-react'
import { useLocaleState } from '@merchant-portal/merchant-portal-base'
import Datepicker from '../../../../../internal/Datepicker'
import Switch from '../../../../../midasComponents/components/Switch'
import Tooltip from '../../../../../internal/Tooltip'
import Badge from '../../../../../internal/Badge'
import DisabledOverlay from '../../../../../internal/DisabledOverlay'
import { delayedPaymentUpdateAmountAndDebitReference } from '../../../../../store/actions/pay-apply-actions'
import { useToggle } from '../../../../../util/utility-hooks'
import ApplicationSignPageDigitalUI from './ApplicationSignPageDigitalUI'
import { getText } from '../../../../../internal/TextLabels'
import { SigningTypes } from '../../../../../util/paymentModel'
import {
  calculateLinkExpirationDate,
  datePickerDateFormat,
} from '../../../../../util/dateUtils'
import { DelayedPaymentSignPageUI } from './DelayedPaymentSignPageUI'
import { requestSigningForOngoingPayment } from '../../../../../store/actions/pay-apply-signing-actions'

import styles from './styles.module.scss'

const SignDigitalPageUI = ({
  debitAmountError,
  onNext,
  customer: propCustomer,
  isLoading,
  storeName,
  setModalOpen,
  paymentId,
  isDelayedPaymentActive,
  delayedPaymentAutomaticDebitDateEnabled,
  authorizationTimeInDays = 90,
  requestSigningForCustomer,
}) => {
  const { language } = useLocaleState()
  const [showDelayedPaymentFrame, toggleDelayPaymentFrame] = useToggle(false)
  const [buttonEnabled, setButtonEnabled] = useState(false)
  const [customer, setCustomer] = useState({
    email: propCustomer.email,
    mobile: propCustomer.mobile,
  })
  const [debitDate, setDebitDate] = useState(null)
  const debitDateConstraints = {
    minDate: moment().add(1, 'days').startOf('day'),
    maxDate: moment().add(authorizationTimeInDays, 'days').startOf('day'),
  }
  const isDebitDateValid = () => {
    if (!delayedPaymentAutomaticDebitDateEnabled) {
      return true
    }
    return (
      debitDate &&
      !debitDate.isBefore(debitDateConstraints.minDate) &&
      !debitDate.isAfter(debitDateConstraints.maxDate)
    )
  }

  useEffect(
    () =>
      customer.valid && isDebitDateValid()
        ? setButtonEnabled(true)
        : setButtonEnabled(false),
    [customer, debitDate],
  )

  useEffect(() => {
    setDebitDate(null)
  }, [showDelayedPaymentFrame])

  const linkExpirationDate = calculateLinkExpirationDate()

  return isDelayedPaymentActive ? (
    <>
      <div style={{ display: 'flex', marginTop: '10px' }}>
        <div>
          <Switch
            boldText
            text={getText('payApply.sign.delayedPayment.toggleButton')}
            id="test"
            required
            onChange={() => toggleDelayPaymentFrame()}
          />
        </div>
        <div>
          <Tooltip
            style={{ left: '35px', width: '500px' }}
            content={getText('payApply.sign.delayedPayment.tooltip')}
          >
            <Badge text="?" />
          </Tooltip>
        </div>
      </div>
      {showDelayedPaymentFrame ? (
        <div>
          {!!delayedPaymentAutomaticDebitDateEnabled && (
            <div className={styles.delayedPaymentDatePicker}>
              <Datepicker
                name="debitDate"
                label={getText(
                  'payApply.sign.delayedPayment.automaticDebitDate.label',
                )}
                selected={debitDate}
                allowEmptyDate
                onChange={date => {
                  setDebitDate(date && moment(date))
                }}
                minDate={debitDateConstraints.minDate}
                maxDate={debitDateConstraints.maxDate}
                dateFormat={datePickerDateFormat(language)}
                validationMessageKeyNotInRange="payApply.sign.delayedPayment.automaticDebitDate.invalidDate"
                validationMessageKeyInvalidFormat=""
                direction="column"
                icon={
                  <svg
                    width="16"
                    height="18"
                    viewBox="0 0 16 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.4001 1.636H13.6001V0H12.0001V1.636H4.00006V0H2.40006V1.636H1.60006C1.17118 1.64206 0.762134 1.81765 0.462324 2.1244C0.162514 2.43114 -0.00368088 2.84409 6.18724e-05 3.273V16.364C-0.00341522 16.7927 0.162897 17.2054 0.462679 17.512C0.76246 17.8185 1.17135 17.9939 1.60006 18H14.4001C14.8288 17.9939 15.2377 17.8185 15.5374 17.512C15.8372 17.2054 16.0035 16.7927 16.0001 16.364V3.273C16.0038 2.84409 15.8376 2.43114 15.5378 2.1244C15.238 1.81765 14.8289 1.64206 14.4001 1.636ZM14.4001 16.363H1.60006V5.727H14.4001V16.363Z"
                      fill="#00807B"
                    />
                  </svg>
                }
              />
              <small>
                {getText(
                  'payApply.sign.delayedPayment.automaticDebitDate.description',
                )}
              </small>
            </div>
          )}
          <DisabledOverlay disable={!!debitAmountError}>
            <DelayedPaymentSignPageUI
              disabled={!!debitAmountError}
              isNewPageLoading={isLoading}
              customer={customer}
              paymentId={paymentId}
              onChange={setCustomer}
            >
              <>
                <h5>{getText('payApply.sign.delayedPayment.messageTitle')}</h5>
                {getText('payApply.sign.delayedPayment.message', {
                  storeName,
                })}
                <br />
                <h5>
                  {getText('payApply.sign.delayedPayment.expirationTitle')}
                </h5>
                {linkExpirationDate}
                <div style={{ textAlign: 'right', marginTop: 25 }}>
                  <Button
                    id="delayed-payment-sign-btn"
                    type="primary"
                    loading={isLoading}
                    disabled={!buttonEnabled}
                    autoFocus
                    onClick={() =>
                      requestSigningForCustomer(
                        paymentId,
                        customer,
                        delayedPaymentAutomaticDebitDateEnabled
                          ? debitDate?.format('YYYY-MM-DD')
                          : null,
                        () =>
                          onNext({
                            selectedSigningMethod: SigningTypes.ELECTRONIC,
                            isDelayPayment: true,
                            automaticDebitDate: debitDate,
                          }),
                      )
                    }
                  >
                    {getText('payApply.sign.delayedPayment.sendLink')}
                  </Button>
                </div>
              </>
            </DelayedPaymentSignPageUI>
          </DisabledOverlay>
        </div>
      ) : (
        <ApplicationSignPageDigitalUI
          onNext={() => {
            setModalOpen(true)
            onNext({ selectedSigningMethod: SigningTypes.ELECTRONIC })
          }}
          newPageLoading={isLoading}
        />
      )}
    </>
  ) : (
    <ApplicationSignPageDigitalUI
      onNext={() => {
        setModalOpen(true)
        onNext({ selectedSigningMethod: SigningTypes.ELECTRONIC })
      }}
      newPageLoading={isLoading}
    />
  )
}

SignDigitalPageUI.propTypes = {
  debitAmountError: PropTypes.bool.isRequired,
  onNext: PropTypes.func.isRequired,
  customer: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  storeName: PropTypes.string.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  requestSigningForCustomer: PropTypes.func.isRequired,
  paymentId: PropTypes.string.isRequired,
  isDelayedPaymentActive: PropTypes.bool.isRequired,
  delayedPaymentAutomaticDebitDateEnabled: PropTypes.bool.isRequired,
  authorizationTimeInDays: PropTypes.number.isRequired,
}

const mapStateToProps = state => ({
  delayedPaymentAutomaticDebitDateEnabled:
    state.storeInfo.currentRepresentative
      .delayedPaymentAutomaticDebitDateEnabled,
  authorizationTimeInDays:
    state.storeInfo.currentRepresentative.authorizationTimeInDays,
})

const mapDispatchToProps = dispatch => ({
  requestSigningForCustomer: (
    paymentId,
    customer,
    automaticDebitDate,
    successfulAction,
  ) =>
    dispatch(delayedPaymentUpdateAmountAndDebitReference).then(() =>
      dispatch(
        requestSigningForOngoingPayment(
          { paymentId, customer, automaticDebitDate },
          successfulAction,
        ),
      ),
    ),
})

export default connect(mapStateToProps, mapDispatchToProps)(SignDigitalPageUI)
